import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DISTRICTS } from '../../data/districts';

// "Explore Your District" instant search. Filters the bundled district config
// (no backend) and jumps straight into /demo/:slug. The strongest sales moment:
// a superintendent types their district and immediately sees the product in
// their colors. `variant`: 'hero' (compact, on dark) | 'section' (large, light).
const DistrictFinder = ({ variant = 'section', placeholder }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return DISTRICTS;
    return DISTRICTS.filter((d) => d.name.toLowerCase().includes(s) || d.city.toLowerCase().includes(s) || d.shortName.toLowerCase().includes(s));
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (d) => { if (d) navigate(`/demo/${d.slug}`); };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const hero = variant === 'hero';

  return (
    <div ref={wrapRef} className="relative w-full max-w-xl mx-auto text-left">
      <div className="relative">
        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-ink-400 text-lg">🏫</span>
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || 'Find your district (e.g. Edinburg CISD)'}
          className="w-full pl-12 pr-5 py-4 rounded-2xl text-base sm:text-lg text-ink-800 bg-white shadow-lift border-none outline-none focus:ring-4 focus:ring-accent-400/40 placeholder:text-ink-400"
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl shadow-lift z-30 overflow-hidden max-h-[340px] overflow-y-auto ring-1 ring-ink-100">
          {results.length === 0 ? (
            <div className="p-4 text-center text-ink-400 text-sm">
              No match. <button onClick={() => navigate('/demo')} className="text-brand-600 font-medium">Browse all districts →</button>
            </div>
          ) : (
            results.map((d, i) => {
              const { primary, secondary, accent } = d.colors;
              const initials = d.shortName ? d.shortName.slice(0, 4) : d.name.slice(0, 2).toUpperCase();
              return (
                <button
                  key={d.slug}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(d)}
                  className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${i === active ? 'bg-brand-50' : 'hover:bg-ink-50'}`}
                >
                  <span
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xs font-extrabold overflow-hidden"
                    style={d.logo ? { backgroundColor: '#fff', boxShadow: 'inset 0 0 0 1px #e2e8f0' } : { background: `linear-gradient(135deg, ${primary}, ${secondary})`, color: accent }}
                  >
                    {d.logo ? <img src={d.logo} alt="" className="w-full h-full object-contain p-0.5" /> : initials}
                  </span>
                  <span className="min-w-0 flex-grow">
                    <span className="block font-semibold text-ink-900 text-sm truncate">{d.name}</span>
                    <span className="block text-xs text-ink-400 truncate">{d.city}</span>
                  </span>
                  <span className="text-brand-500 text-sm font-medium flex-shrink-0">View demo →</span>
                </button>
              );
            })
          )}
        </div>
      )}

      {hero && (
        <p className="mt-2 text-sm text-brand-200 text-center">
          {DISTRICTS.length} Rio Grande Valley districts ready to preview.
        </p>
      )}
    </div>
  );
};

export default DistrictFinder;
